import moment from 'moment';

import generalConstants from 'src/constants/general';
import { formatDateRange } from 'src/helpers/general';
import { lang } from 'src/helpers/lang';
import LANG_CONST from 'src/helpers/lang/const';

const accountStates = generalConstants.accountStates;
const onlineStatusTimeoutMs = 60 * 1000;
const justNowStatusWindowMs = onlineStatusTimeoutMs * 2;

const toActivityMoment = (date) => (moment.isMoment(date) ? date.clone() : moment(date));

const getActivityAgeMs = (date, now = moment()) => {
  const activityMoment = toActivityMoment(date);

  if (!activityMoment.isValid()) return null;

  return now.diff(activityMoment);
};

const isOnlineNow = (date, now = moment()) => {
  const activityMoment = toActivityMoment(date);

  if (!activityMoment.isValid()) return false;

  return activityMoment.isAfter(now) || now.diff(activityMoment) < onlineStatusTimeoutMs;
};

export const getUnavailableStateForNow = (accountState, future = false) => {
  if (accountState?.state !== accountStates.not_available) return null;
  if (!accountState?.startDate) return { start: null, end: null };

  const start = moment(accountState.startDate);
  const end = moment(accountState.endDate || accountState.startDate);

  if (moment().isBetween(start, end, 'date', '[]') || (future && start.isAfter(moment()))) {
    return { start, end };
  }

  return null;
};

export const formatLastActivity = (date) => {
  const activityMoment = toActivityMoment(date).locale(moment.locale());
  const now = moment().locale(moment.locale());
  const localeData = activityMoment.localeData();
  const ageMs = getActivityAgeMs(activityMoment, now);

  if (!activityMoment.isValid()) return '';
  if (activityMoment.isAfter(now)) {
    return '';
  }

  if (ageMs !== null && ageMs < justNowStatusWindowMs) {
    return lang.t('Last seen just now');
  }

  const minutes = now.diff(activityMoment, 'minutes');
  if (minutes < 60) {
    return lang.t('Last seen {:time}', { time: activityMoment.from(now) });
  }

  const hours = now.diff(activityMoment, 'hours');
  if (hours < 12) {
    return lang.t('Last seen {:time}', { time: activityMoment.from(now) });
  }

  if (
    activityMoment.isSame(now, 'day') ||
    activityMoment.isSame(now.clone().subtract(1, 'day'), 'day')
  ) {
    if (activityMoment.isSame(now, 'day')) {
      return lang.t('Last seen today at {:time}', {
        time: activityMoment.format(localeData.longDateFormat('LT'))
      });
    }

    return lang.t('Last seen yesterday at {:time}', {
      time: activityMoment.format(localeData.longDateFormat('LT'))
    });
  }

  return lang.t('Last seen {:date}', {
    date: activityMoment.format(localeData.longDateFormat('L'))
  });
};

export const getLastActivityLabel = ({ accountState, lastActivity }) => {
  const nowState = getUnavailableStateForNow(accountState);

  if (nowState) {
    if (!nowState.start) return lang.t(LANG_CONST.unavailable);
    return lang.t(LANG_CONST.unavailable) + ' ' + formatDateRange(nowState.start, nowState.end);
  }

  if (isOnlineNow(lastActivity)) {
    return lang.t(LANG_CONST.online);
  }

  const lastActivityLabel = formatLastActivity(lastActivity);
  if (lastActivityLabel) return lastActivityLabel;

  return lang.t(LANG_CONST.offline);
};
